import { Link, useLocation, useNavigate, useNavigationType, useSearchParams } from "react-router-dom";
import ApplicantShell from "../components/ApplicantShell";
import ApplicantJobSearchResultCard from "../components/ApplicantJobSearchResultCard";
import PageSeo from "../components/PageSeo";
import JobListingContent from "../components/JobListingContent";
import JobPostCommentsSection from "../components/JobPostCommentsSection";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Briefcase, Heart, SlidersHorizontal } from "lucide-react";
import WorkCountriesPillPicker from "../components/WorkCountriesPillPicker";
import SiteBreadcrumbs from "../components/SiteBreadcrumbs";
import PitchRecordModal from "../components/PitchRecordModal";
import { captureAnalyticsEvent } from "../lib/analytics";
import SalaryCurrencyIcon from "../components/SalaryCurrencyIcon";
import { formatJobApplicationDeadline, SALARY_CURRENCY_OPTIONS } from "./applicantData";
import { APPLICANT_COUNTRY_GROUPS } from "../data/applicantCountryGroups";
import {
  deleteApplicantApplicationPitchVideo,
  deleteApplicantJobPostNotForMe,
  deleteApplicantJobPostSaved,
  getApplicantMyApplicationForJobPost,
  isApiConfigured,
  listApplicantSavedSearches,
  postApplicantApplicationPitchVideo,
  postApplicantJobPostInteractionStatus,
  postApplicantJobSearch,
  postPublicJobSearch,
  postApplicantSavedSearch,
  putApplicantJobPostNotForMe,
  putApplicantJobPostSaved,
} from "../apiClient";
import { JOB_SEARCH_RESTORE_STORAGE_KEY } from "../navigation/scrollRestorePolicy";
import {
  isJobSearchRestoreUiState,
  jobSearchRestoreScrollY,
  readJobSearchRestoreSnapshot,
  resolveJobSearchRestoreUi,
} from "../jobSearch/jobSearchRestore";
import {
  buildJobSearchParamsFromState,
  canonicalJobSearchQueryStringFromLocationSearch,
  jobSearchUrlHasAnyCriteria,
  jobSearchUrlHasRunnableCriteria,
  parseJobSearchUrlFragment,
} from "../jobSearch/jobSearchUrlParams";
import { buildAuthHrefWithNext } from "../navigateAfterAuth";
import { isApplicantProfileSetupComplete } from "../lib/applicantProfileNotification";
import { jobPublicHref } from "../lib/jobPublicPath";
import PillRadioFieldset from "../components/PillRadioFieldset";

const PAGE_SIZE = 6;
/** Load this many pages per search so results are not stuck at PAGE_SIZE when scroll/sentinel does not fire. */
const MAX_APPLICANT_JOB_SEARCH_AUTO_PAGES = 15;
const MAX_APPLICANT_SAVED_SEARCHES = 8;
const JOB_POST_ID_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Must stay at or below API `MAX_INTERACTION_STATUS_IDS` (100). */
const INTERACTION_STATUS_CHUNK = 80;

const JOB_SEARCH_SALARY_CURRENCY_PILL_OPTIONS = SALARY_CURRENCY_OPTIONS.map((o) => ({
  value: o.value,
  label: `${o.symbol} ${o.value}`,
  secondary: o.name,
}));

const JOB_SEARCH_WORK_MODE_PILL_OPTIONS = [
  { value: "", label: "Any", secondary: "Remote, hybrid, onsite" },
  { value: "remote", label: "Remote Only", secondary: "" },
  { value: "hybrid", label: "Hybrid Only", secondary: "" },
  { value: "onsite", label: "Onsite Only", secondary: "" },
];

function formatAppliedOnFromIso(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function formatStatusCompletedAtFromIso(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function interactionTimestampIsoFromApi(flag, raw) {
  if (!flag || raw == null || raw === "") return "";
  if (typeof raw === "string") return raw.trim();
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}

function emptyJobPostInteraction() {
  return {
    notForMe: false,
    saved: false,
    applied: false,
    appliedOn: "",
    appliedAtIso: "",
    savedAtIso: "",
    notForMeAtIso: "",
  };
}

function normalizeByJobPostIdFromApi(byJobPostId) {
  const out = {};
  if (!byJobPostId || typeof byJobPostId !== "object") return out;
  for (const [id, row] of Object.entries(byJobPostId)) {
    const appliedAtIso = interactionTimestampIsoFromApi(row.applied, row.appliedAt);
    out[id] = {
      notForMe: Boolean(row.notForMe),
      saved: Boolean(row.saved),
      applied: Boolean(row.applied),
      appliedOn: row.applied ? formatAppliedOnFromIso(appliedAtIso || row.appliedAt) : "",
      appliedAtIso,
      savedAtIso: interactionTimestampIsoFromApi(row.saved, row.savedAt),
      notForMeAtIso: interactionTimestampIsoFromApi(row.notForMe, row.notForMeAt),
    };
  }
  return out;
}
/** Shown when country list or keyword pills are empty before search. */
const JOB_SEARCH_COUNTRY_AND_KEYWORD_HINT = "Need at least one country and one keyword";
const JOB_SEARCH_REQUIRES_API_MESSAGE = "Job search requires the API to be configured.";
const SEARCH_RESULTS_LABEL = "Search results";
const APPLY_HELP_SAMPLE_VIDEO =
  "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4";

const EEA_COUNTRY_GROUP = APPLICANT_COUNTRY_GROUPS.find((g) => g.id === "EEA") ?? null;
const MAX_COUNTRY_CODES = 50;
const SALARY_CURRENCY_VALUE_SET = new Set(SALARY_CURRENCY_OPTIONS.map((o) => o.value));
const MAX_KEYWORD_PILLS = 5;
const MAX_KEYWORD_CHARS = 200;
const EU_MEMBER_CODES = new Set([
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
]);

function hasSalaryCurrency(value) {
  const c = String(value ?? "").trim().toUpperCase();
  return SALARY_CURRENCY_VALUE_SET.has(c);
}

/**
 * Same rules as job search before calling the API (when `requireSalary` is true).
 * @param {{ countryCodes: string[], keywords: string[], salaryCurrency: string, salaryMin: number, salaryMax: number }} payload
 * @param {boolean} requireSalary — when true, validate salary currency if a salary bound is set.
 * @returns {string | null}
 */
function getSearchCriteriaValidationMessage(payload, requireSalary) {
  if (!payload.countryCodes?.length || !payload.keywords?.length) {
    return JOB_SEARCH_COUNTRY_AND_KEYWORD_HINT;
  }
  if (!requireSalary) return null;
  const hasSalaryBound =
    Number.isFinite(payload.salaryMin) || Number.isFinite(payload.salaryMax);
  if (!hasSalaryBound) return null;
  if (!hasSalaryCurrency(payload.salaryCurrency)) {
    return "Choose a salary currency.";
  }
  if (
    Number.isFinite(payload.salaryMin) &&
    Number.isFinite(payload.salaryMax) &&
    payload.salaryMin > payload.salaryMax
  ) {
    return "Salary minimum cannot be greater than salary maximum.";
  }
  return null;
}

function defaultCurrencyFromResidenceIso(iso) {
  const c = String(iso || "").trim().toUpperCase();
  if (c === "AU") return "AUD";
  if (c === "CA") return "CAD";
  if (c === "GB") return "GBP";
  if (EU_MEMBER_CODES.has(c)) return "EUR";
  return "USD";
}

/** Align stored criteria with `<select name="workMode">` values: `"" | remote | hybrid | onsite`. */
function normalizeCriteriaWorkModeForSelect(raw) {
  const s = String(raw ?? "").trim().toLowerCase();
  if (s === "remote" || s === "hybrid" || s === "onsite") return s;
  return "";
}

function parseKeywordSegments(serialized) {
  return String(serialized || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, MAX_KEYWORD_PILLS);
}

function serializeKeywordSegments(segments) {
  return segments.join(", ");
}

function clampKeywordSegmentsToCharBudget(segments, maxChars) {
  const out = [];
  for (const s of segments) {
    const next = [...out, s];
    if (serializeKeywordSegments(next).length > maxChars) break;
    out.push(s);
  }
  return out;
}

const KeywordPillsInput = forwardRef(function KeywordPillsInput(
  { composeId, value, onChange, placeholder, ariaLabel = "Add keyword" },
  ref
) {
  const pills = useMemo(() => parseKeywordSegments(value), [value]);
  const [draft, setDraft] = useState("");
  const draftRef = useRef("");
  const valueRef = useRef(value);
  valueRef.current = value;
  draftRef.current = draft;

  useImperativeHandle(
    ref,
    () => ({
      flushDraftAndGetSerialized() {
        const frag = String(draftRef.current || "").trim();
        let next = [...parseKeywordSegments(valueRef.current)];
        if (frag && next.length < MAX_KEYWORD_PILLS) {
          next = [...next, frag].slice(0, MAX_KEYWORD_PILLS);
        }
        setDraft("");
        const clamped = clampKeywordSegmentsToCharBudget(next, MAX_KEYWORD_CHARS);
        const serialized = serializeKeywordSegments(clamped);
        const prev = String(valueRef.current || "").trim();
        if (serialized !== prev) {
          onChange({ target: { value: serialized } });
        }
        return serialized;
      },
      clearComposeDraft() {
        setDraft("");
      },
    }),
    [onChange]
  );

  const commitDraft = () => {
    const frag = draft.trim();
    if (!frag) return;
    if (pills.length >= MAX_KEYWORD_PILLS) {
      setDraft("");
      return;
    }
    let next = [...pills, frag].slice(0, MAX_KEYWORD_PILLS);
    setDraft("");
    const clamped = clampKeywordSegmentsToCharBudget(next, MAX_KEYWORD_CHARS);
    onChange({ target: { value: serializeKeywordSegments(clamped) } });
  };

  const removePill = (index) => {
    const next = pills.filter((_, i) => i !== index);
    onChange({ target: { value: serializeKeywordSegments(next) } });
  };

  const onComposeChange = (e) => {
    const raw = e.target.value;
    if (!raw.includes(",")) {
      setDraft(raw);
      return;
    }
    const parts = raw.split(",");
    let working = [...pills];
    for (let i = 0; i < parts.length - 1; i++) {
      const seg = parts[i].trim();
      if (!seg) continue;
      if (working.length >= MAX_KEYWORD_PILLS) break;
      working.push(seg);
    }
    working = clampKeywordSegmentsToCharBudget(working, MAX_KEYWORD_CHARS);
    const tail = parts[parts.length - 1] ?? "";
    setDraft(tail);
    const prevSerialized = serializeKeywordSegments(pills);
    const nextSerialized = serializeKeywordSegments(working);
    if (nextSerialized !== prevSerialized) {
      onChange({ target: { value: nextSerialized } });
    }
  };

  const onComposeKeyDown = (e) => {
    if (e.key === "," || e.key === "Enter") {
      e.preventDefault();
      commitDraft();
      return;
    }
    if (e.key === "Backspace" && draft === "") {
      if (!pills.length) return;
      e.preventDefault();
      const next = pills.slice(0, -1);
      onChange({ target: { value: serializeKeywordSegments(next) } });
    }
  };

  const onComposeBlur = () => {
    commitDraft();
  };

  return (
    <div className="job-create-functional-area-shell">
      {pills.map((pill, index) => (
        <span key={`${index}:${pill}`} className="job-create-functional-area-pill">
          {pill}
          <button
            type="button"
            className="job-create-functional-area-pill-remove"
            aria-label={`Remove ${pill}`}
            onClick={() => removePill(index)}
          >
            ×
          </button>
        </span>
      ))}
      <input
        id={composeId}
        type="text"
        className="job-create-functional-area-compose"
        value={draft}
        onChange={onComposeChange}
        onKeyDown={onComposeKeyDown}
        onBlur={onComposeBlur}
        placeholder={pills.length === 0 && !draft ? placeholder : ""}
        aria-label={ariaLabel}
      />
    </div>
  );
});

KeywordPillsInput.displayName = "KeywordPillsInput";
const RESULT_STATUS_OPTIONS = [
  null,
  { key: "applied", iconKey: "applied", label: "Applied" },
  { key: "not-for-me", iconKey: "not-for-me", label: "Marked as not for me" },
  { key: "saved", iconKey: "saved", label: "Saved" },
];

function GuideIcon({ name, className = "icon", ...rest }) {
  if (name === "row-view") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M3 6.5a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1zm0 8a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "square-view") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M3 9.4c0-2.24 0-3.36.436-4.216a4 4 0 0 1 1.748-1.748C6.04 3 7.16 3 9.4 3h5.2c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C21 6.04 21 7.16 21 9.4v5.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C17.96 21 16.84 21 14.6 21H9.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C3 17.96 3 16.84 3 14.6z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "not-for-me") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="m18 9-.84 8.398c-.127 1.273-.19 1.909-.48 2.39a2.5 2.5 0 0 1-1.075.973C15.098 21 14.46 21 13.18 21h-2.36c-1.279 0-1.918 0-2.425-.24a2.5 2.5 0 0 1-1.076-.973c-.288-.48-.352-1.116-.48-2.389L6 9m7.5 6.5v-5m-3 5v-5m-6-4h4.615m0 0 .386-2.672c.112-.486.516-.828.98-.828h3.038c.464 0 .867.342.98.828l.386 2.672m-5.77 0h5.77m0 0H19.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "applied") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M9.713 3.64c.581-.495.872-.743 1.176-.888a2.58 2.58 0 0 1 2.222 0c.304.145.595.393 1.176.888.599.51 1.207.768 2.007.831.761.061 1.142.092 1.46.204.734.26 1.312.837 1.571 1.572.112.317.143.698.204 1.46.063.8.32 1.407.83 2.006.496.581.744.872.889 1.176.336.703.336 1.52 0 2.222-.145.304-.393.595-.888 1.176a3.3 3.3 0 0 0-.831 2.007c-.061.761-.092 1.142-.204 1.46a2.58 2.58 0 0 1-1.572 1.571c-.317.112-.698.143-1.46.204-.8.063-1.407.32-2.006.83-.581.496-.872.744-1.176.889a2.58 2.58 0 0 1-2.222 0c-.304-.145-.595-.393-1.176-.888a3.3 3.3 0 0 0-2.007-.831c-.761-.061-1.142-.092-1.46-.204a2.58 2.58 0 0 1-1.571-1.572c-.112-.317-.143-.698-.204-1.46a3.3 3.3 0 0 0-.83-2.006c-.496-.581-.744-.872-.89-1.176a2.58 2.58 0 0 1 .001-2.222c.145-.304.393-.595.888-1.176.52-.611.769-1.223.831-2.007.061-.761.092-1.142.204-1.46a2.58 2.58 0 0 1 1.572-1.571c.317-.112.698-.143 1.46-.204a3.3 3.3 0 0 0 2.006-.83"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="m8.667 12.633 1.505 1.721a1 1 0 0 0 1.564-.073L15.333 9.3"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "saved") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "chevron-left") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path d="m15 6-6 6 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "chevron-right") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "close") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "play") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M13.242 10.71c.672.547 1.008.821 1.008 1.29s-.336.743-1.008 1.29c-.185.152-.37.295-.538.413a9 9 0 0 1-.49.318c-.67.407-1.006.611-1.306.385s-.328-.697-.383-1.642A14 14 0 0 1 10.5 12c0-.235.01-.497.025-.764.055-.945.082-1.417.383-1.642.3-.226.636-.022 1.306.385.174.106.341.214.49.318.169.118.353.261.538.412"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "minimise") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M20 10h-6V4M4 14h6v6M20 4l-6 6m-4 4-6 6"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "maximise") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M14 4h6v6M10 20H4v-6M20 4l-6 6M4 20l6-6"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "save") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M16.25 21v-4.765a1.59 1.59 0 0 0-1.594-1.588H9.344a1.59 1.59 0 0 0-1.594 1.588V21m8.5-17.715v2.362a1.59 1.59 0 0 1-1.594 1.588H9.344A1.59 1.59 0 0 1 7.75 5.647V3m8.5.285A3.2 3.2 0 0 0 14.93 3H7.75m8.5.285c.344.156.661.374.934.645l2.382 2.375A3.17 3.17 0 0 1 20.5 8.55v9.272A3.18 3.18 0 0 1 17.313 21H6.688A3.18 3.18 0 0 1 3.5 17.823V6.176A3.18 3.18 0 0 1 6.688 3H7.75"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "check") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="m6 13.626 1.606 1.722c.886.95 1.329 1.424 1.825 1.574.436.131.9.096 1.315-.1.473-.224.852-.761 1.612-1.836L18 7"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "info") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 16v-5h-.5m0 5h1M12 8.5V8"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "locations") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M12 21a9 9 0 1 0 0-18m0 18a9 9 0 1 1 0-18m0 18c2.761 0 3.941-5.163 3.941-9S14.761 3 12 3m0 18c-2.761 0-3.941-5.163-3.941-9S9.239 3 12 3M3.5 9h17m-17 6h17"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "email") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="m2.357 7.714 6.98 4.654c.963.641 1.444.962 1.964 1.087.46.11.939.11 1.398 0 .52-.125 1.001-.446 1.964-1.087l6.98-4.654M7.157 19.5h9.686c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.31-1.311c.328-.642.328-1.482.328-3.162V9.3c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311c-.642-.327-1.482-.327-3.162-.327H7.157c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.31 1.311c-.328.642-.328 1.482-.328 3.162v5.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311c.642.327 1.482.327 3.162.327"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "mobile") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M16.286 3H7.714C6.768 3 6 3.806 6 4.8v14.4c0 .994.768 1.8 1.714 1.8h8.572c.947 0 1.714-.806 1.714-1.8V4.8c0-.994-.767-1.8-1.714-1.8M10.5 6h3"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "facebook") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="M6.5 10v4h3v7h4v-7h3l1-4h-4V8c0-.545.455-1 1-1h3V3h-3c-2.723 0-5 2.277-5 5v2z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "linkedin") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <title>LinkedIn</title>
        <path
          d="M3 9.4c0-2.24 0-3.36.436-4.216a4 4 0 0 1 1.748-1.748C6.04 3 7.16 3 9.4 3h5.2c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C21 6.04 21 7.16 21 9.4v5.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C17.96 21 16.84 21 14.6 21H9.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C3 17.96 3 16.84 3 14.6zm5-1.775v.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8 16.375V10.75m4 5.625V13.5m0 0v-2.75m0 2.75c0-1.288 1.222-2 2.4-2 1.6 0 1.6 1.375 1.6 2.875v2"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "instagram") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <title>Instagram</title>
        <path
          d="M3 9.4c0-2.24 0-3.36.436-4.216a4 4 0 0 1 1.748-1.748C6.04 3 7.16 3 9.4 3h5.2c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C21 6.04 21 7.16 21 9.4v5.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C17.96 21 16.84 21 14.6 21H9.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C3 17.96 3 16.84 3 14.6zm14-2.9h.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M15.462 11.487a3.5 3.5 0 1 1-6.925 1.026 3.5 3.5 0 0 1 6.925-1.026"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "link") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <path
          d="m15.988 13 3.902-3.902c1.437-1.437 1.485-3.718.107-5.095-1.377-1.378-3.658-1.33-5.095.107L11 8.012m2 7.95-3.892 3.88c-1.432 1.43-3.64 1.615-5.082.107-1.442-1.507-1.326-3.639.107-5.068L8.025 11M9 15l6-6"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "youtube") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <title>YouTube</title>
        <path
          d="M2 12c0-3.3 0-4.95 1.464-5.975C4.93 5 7.286 5 12 5s7.071 0 8.535 1.025S22 8.7 22 12s0 4.95-1.465 5.975C19.072 19 16.714 19 12 19s-7.071 0-8.536-1.025S2 15.3 2 12"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M10.5 9.908v4.184a.41.41 0 0 0 .412.408.4.4 0 0 0 .228-.068l3.175-2.074a.405.405 0 0 0 .003-.678l-3.175-2.11a.415.415 0 0 0-.573.11.4.4 0 0 0-.07.228"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (name === "refresh") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" {...rest}>
        <title>Refresh</title>
        <path
          d="M20.5 8c-1.392-3.179-4.823-5-8.522-5C7.299 3 3.453 6.552 3 11.1"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M16.489 8.4h3.97A.54.54 0 0 0 21 7.86V3.9M3.5 16c1.392 3.179 4.823 5 8.522 5 4.679 0 8.525-3.552 8.978-8.1"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7.511 15.6h-3.97a.54.54 0 0 0-.541.54v3.96"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return null;
}

function JobSearchInteractionStatusRow({ status, interaction }) {
  if (!status) return null;
  const intr = interaction ?? emptyJobPostInteraction();
  let iso = "";
  if (status.key === "applied") iso = intr.appliedAtIso || "";
  else if (status.key === "saved") iso = intr.savedAtIso || "";
  else if (status.key === "not-for-me") iso = intr.notForMeAtIso || "";
  const timeLabel = formatStatusCompletedAtFromIso(iso);
  const ariaParts = [status.label, timeLabel].filter(Boolean);
  return (
    <div
      className="job-search-result-status-row"
      role="group"
      data-interaction-status={status.key}
      aria-label={ariaParts.join(". ")}
    >
      <span className={`job-result-status status-${status.key}`} aria-hidden="true">
        {status.key === "saved" ? (
          <Heart
            className="job-result-status-icon job-result-status-icon--saved"
            size={16}
            strokeWidth={1.75}
            fill="currentColor"
            aria-hidden="true"
          />
        ) : (
          <GuideIcon name={status.iconKey} className="job-result-status-icon" />
        )}
      </span>
      <span className="job-result-status-label">{status.label}</span>
      {timeLabel ? (
        <time className="job-result-status-time" dateTime={iso}>
          {timeLabel}
        </time>
      ) : null}
    </div>
  );
}

function JobSearchPage({ currentUser, setCurrentUser, authToken }) {
  const navigate = useNavigate();
  const location = useLocation();
  const jobSearchReturnNext = useMemo(
    () => `${location.pathname}${location.search}`,
    [location.pathname, location.search]
  );
  const signInWithNextHref = useMemo(
    () => buildAuthHrefWithNext("/signin", jobSearchReturnNext),
    [jobSearchReturnNext]
  );
  const signUpWithNextHref = useMemo(
    () => buildAuthHrefWithNext("/signup", jobSearchReturnNext),
    [jobSearchReturnNext]
  );
  const [, setSearchParams] = useSearchParams();
  const navType = useNavigationType();
  const initialSearchState = useMemo(() => {
    const urlFragment = parseJobSearchUrlFragment(location.search);
    const fromRouter = location.state?.searchUiState;

    if (fromRouter && typeof fromRouter === "object") {
      return {
        ...urlFragment,
        ...fromRouter,
        executeSearchOnMount: fromRouter.executeSearchOnMount,
      };
    }

    if (jobSearchUrlHasRunnableCriteria(urlFragment)) {
      return {
        ...urlFragment,
        hasSearched: false,
      };
    }

    if (jobSearchUrlHasAnyCriteria(urlFragment)) {
      return {
        ...urlFragment,
        hasSearched: false,
      };
    }

    if (navType === "POP") {
      try {
        const raw = window.sessionStorage.getItem(JOB_SEARCH_RESTORE_STORAGE_KEY);
        if (!raw) {
          return Object.keys(urlFragment).length ? { ...urlFragment, hasSearched: false } : undefined;
        }
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") {
          return Object.keys(urlFragment).length ? { ...urlFragment, hasSearched: false } : undefined;
        }
        if (Object.keys(urlFragment).length > 0) {
          return { ...parsed, ...urlFragment };
        }
        return parsed;
      } catch {
        return Object.keys(urlFragment).length ? { ...urlFragment, hasSearched: false } : undefined;
      }
    }

    return Object.keys(urlFragment).length ? { ...urlFragment, hasSearched: false } : undefined;
  }, [location.state, location.search, navType]);
  const residenceIso = String(currentUser?.countryOfResidence || "").trim().toUpperCase();
  const criteriaFormRef = useRef(null);
  const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState("");
  const [saveSearchError, setSaveSearchError] = useState("");
  const [saveSearchBusy, setSaveSearchBusy] = useState(false);
  const [savedSearchCount, setSavedSearchCount] = useState(null);
  const [savedSearchListLoading, setSavedSearchListLoading] = useState(false);
  const [criteriaCountryCodes, setCriteriaCountryCodes] = useState(
    Array.isArray(initialSearchState?.criteriaCountryCodes) ? initialSearchState.criteriaCountryCodes : []
  );
  const [criteriaCountryInputDraft, setCriteriaCountryInputDraft] = useState("");
  const [criteriaTitles, setCriteriaTitles] = useState(initialSearchState?.criteriaTitles || "");
  const [criteriaWorkMode, setCriteriaWorkMode] = useState(() =>
    normalizeCriteriaWorkModeForSelect(initialSearchState?.criteriaWorkMode ?? initialSearchState?.workMode)
  );
  const [criteriaSalaryCurrency, setCriteriaSalaryCurrency] = useState(
    initialSearchState?.criteriaSalaryCurrency ||
      (residenceIso ? defaultCurrencyFromResidenceIso(residenceIso) : "GBP")
  );
  const [criteriaSalaryMin, setCriteriaSalaryMin] = useState(() =>
    initialSearchState?.salaryMin != null && String(initialSearchState.salaryMin).trim() !== ""
      ? String(initialSearchState.salaryMin).trim()
      : ""
  );
  const [criteriaSalaryMax, setCriteriaSalaryMax] = useState(() =>
    initialSearchState?.salaryMax != null && String(initialSearchState.salaryMax).trim() !== ""
      ? String(initialSearchState.salaryMax).trim()
      : ""
  );
  const [hasSearched, setHasSearched] = useState(Boolean(initialSearchState?.hasSearched));
  const [visibleCount, setVisibleCount] = useState(
    initialSearchState?.visibleCount || PAGE_SIZE
  );
  const [showEndMessage, setShowEndMessage] = useState(Boolean(initialSearchState?.showEndMessage));
  const [viewedJobIds, setViewedJobIds] = useState(initialSearchState?.viewedJobIds || []);
  const [showNotForMe, setShowNotForMe] = useState(initialSearchState?.showNotForMe ?? true);
  const [showApplied, setShowApplied] = useState(initialSearchState?.showApplied ?? true);
  const [showSaved, setShowSaved] = useState(initialSearchState?.showSaved ?? true);
  const [resultsView, setResultsView] = useState(initialSearchState?.resultsView === "tab" ? "tab" : "list");

  useEffect(() => {
    if (!initialSearchState || typeof initialSearchState !== "object") return;
    if ("showNotForMe" in initialSearchState) setShowNotForMe(Boolean(initialSearchState.showNotForMe));
    if ("showApplied" in initialSearchState) setShowApplied(Boolean(initialSearchState.showApplied));
    if ("showSaved" in initialSearchState) setShowSaved(Boolean(initialSearchState.showSaved));
    if (Object.prototype.hasOwnProperty.call(initialSearchState, "salaryMin")) {
      const v = initialSearchState.salaryMin;
      setCriteriaSalaryMin(v != null && String(v).trim() !== "" ? String(v).trim() : "");
    }
    if (Object.prototype.hasOwnProperty.call(initialSearchState, "salaryMax")) {
      const v = initialSearchState.salaryMax;
      setCriteriaSalaryMax(v != null && String(v).trim() !== "" ? String(v).trim() : "");
    }
    if (Object.prototype.hasOwnProperty.call(initialSearchState, "criteriaWorkMode")) {
      setCriteriaWorkMode(normalizeCriteriaWorkModeForSelect(initialSearchState.criteriaWorkMode));
    } else if (Object.prototype.hasOwnProperty.call(initialSearchState, "workMode")) {
      setCriteriaWorkMode(normalizeCriteriaWorkModeForSelect(initialSearchState.workMode));
    }
  }, [initialSearchState]);
  const [tabResultIndex, setTabResultIndex] = useState(() =>
    Number.isFinite(initialSearchState?.tabResultIndex) ? Math.max(0, Math.floor(initialSearchState.tabResultIndex)) : 0
  );
  const [showApplyHelp, setShowApplyHelp] = useState(false);
  const [showApplyShineModal, setShowApplyShineModal] = useState(false);
  const [showProfileIncomplete, setShowProfileIncomplete] = useState(false);
  const [applyShineTargetJobId, setApplyShineTargetJobId] = useState(null);
  const keywordPillsRef = useRef(null);

  const [apiSearchJobs, setApiSearchJobs] = useState(() =>
    Array.isArray(initialSearchState?.apiSearchJobs) ? initialSearchState.apiSearchJobs : []
  );
  const [apiSearchPage, setApiSearchPage] = useState(() => ({
    hasMore: Boolean(initialSearchState?.apiSearchHasMore),
    nextCursor:
      initialSearchState?.apiSearchNextCursor != null && initialSearchState.apiSearchNextCursor !== ""
        ? initialSearchState.apiSearchNextCursor
        : null,
  }));
  const [apiSearchLoading, setApiSearchLoading] = useState(false);
  const [apiSearchError, setApiSearchError] = useState("");
  const useLiveJobSearch = isApiConfigured();
  const isApplicantJobSearch = currentUser?.role === "applicant";
  const [jobPostInteractionById, setJobPostInteractionById] = useState({});
  const jobPostInteractionByIdRef = useRef({});
  useLayoutEffect(() => {
    jobPostInteractionByIdRef.current = jobPostInteractionById;
  }, [jobPostInteractionById]);
  const savedSearchLimitReached =
    isApplicantJobSearch && savedSearchCount != null && savedSearchCount >= MAX_APPLICANT_SAVED_SEARCHES;

  const interactionFetchKey = useMemo(() => {
    if (!hasSearched) return "";
    const ids = [...new Set((apiSearchJobs || []).map((j) => String(j.id)))]
      .filter((id) => JOB_POST_ID_UUID_RE.test(id))
      .sort();
    return ids.join(",");
  }, [hasSearched, apiSearchJobs]);

  useEffect(() => {
    if (!hasSearched || !useLiveJobSearch || currentUser?.role !== "applicant") {
      setJobPostInteractionById({});
      return undefined;
    }
    const token = String(window.localStorage.getItem("jobuwin-auth-token") || "").trim();
    if (!token || !interactionFetchKey) {
      setJobPostInteractionById({});
      return undefined;
    }
    let cancelled = false;
    const ids = interactionFetchKey.split(",").filter(Boolean);
    (async () => {
      try {
        const merged = {};
        for (let i = 0; i < ids.length; i += INTERACTION_STATUS_CHUNK) {
          const chunk = ids.slice(i, i + INTERACTION_STATUS_CHUNK);
          if (!chunk.length) continue;
          const by = await postApplicantJobPostInteractionStatus(chunk, token);
          Object.assign(merged, normalizeByJobPostIdFromApi(by));
        }
        if (!cancelled) setJobPostInteractionById(merged);
      } catch {
        /* Keep existing flags; do not wipe UI on a failed batch (e.g. transient network). */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasSearched, useLiveJobSearch, currentUser?.role, interactionFetchKey]);

  const sentinelRef = useRef(null);
  /** Bumps on each live search start so in-flight page chains do not apply after a newer search. */
  const applicantSearchSeqRef = useRef(0);
  const wasIntersectingRef = useRef(false);
  const tabTouchStartXRef = useRef(null);
  const applyHelpVideoRef = useRef(null);
  const [tabApplicationPitch, setTabApplicationPitch] = useState({ jobId: null, url: "", loaded: false });
  const [tabApplicationPitchDeleteBusy, setTabApplicationPitchDeleteBusy] = useState(false);
  const [tabApplicationPitchDeleteError, setTabApplicationPitchDeleteError] = useState("");
  const lazyPhaseRef = useRef(
    initialSearchState?.lazyPhase != null && Number.isFinite(Number(initialSearchState.lazyPhase))
      ? Math.max(0, Math.min(2, Math.floor(Number(initialSearchState.lazyPhase))))
      : initialSearchState?.showEndMessage
        ? 2
        : initialSearchState?.visibleCount > PAGE_SIZE
          ? 1
          : 0
  );
  const pendingReturnFocusJobIdRef = useRef(
    typeof initialSearchState?.lastOpenedJobId === "string" && initialSearchState.lastOpenedJobId.trim()
      ? initialSearchState.lastOpenedJobId.trim()
      : null
  );
  /** Avoid re-applying scroll for the same navigation entry. */
  const scrollRestoredForLocationKeyRef = useRef(null);
  /** Dedupes `executeSearchOnMount` (e.g. React Strict Mode double effect). */
  const executeLastSearchMountKeyRef = useRef(null);
  /** Query string (no leading `?`) last written or matched — avoids duplicate URL-triggered searches. */
  const jobSearchUrlSyncedRef = useRef("");
  /** Prevents overlapping URL-triggered searches (e.g. React Strict Mode double effect). */
  const urlAutoSearchInFlightRef = useRef(false);

  const buildApplicantJobSearchPayload = useCallback(
    (cursor = null, limit = PAGE_SIZE) => {
      const resolvedTitles = keywordPillsRef.current?.flushDraftAndGetSerialized?.() ?? criteriaTitles;
      const keywords = parseKeywordSegments(resolvedTitles);
      const salaryMin =
        String(criteriaSalaryMin ?? "").trim() === "" ? NaN : Number(String(criteriaSalaryMin).trim());
      const salaryMax =
        String(criteriaSalaryMax ?? "").trim() === "" ? NaN : Number(String(criteriaSalaryMax).trim());
      const workMode = String(criteriaWorkMode || "").trim().toLowerCase();
      const payload = {
        countryCodes: criteriaCountryCodes.map((c) => String(c || "").trim().toUpperCase()).filter(Boolean),
        keywords,
        salaryCurrency: String(criteriaSalaryCurrency || "GBP").trim().toUpperCase(),
        salaryMin,
        salaryMax,
        limit,
        showNotForMe,
        showApplied,
        showSaved,
        resultsView,
      };
      if (["remote", "hybrid", "onsite"].includes(workMode)) {
        payload.workMode = workMode;
      }
      if (cursor) payload.cursor = cursor;
      return payload;
    },
    [
      criteriaCountryCodes,
      criteriaSalaryCurrency,
      criteriaSalaryMax,
      criteriaSalaryMin,
      criteriaTitles,
      criteriaWorkMode,
      resultsView,
      showApplied,
      showNotForMe,
      showSaved,
    ]
  );

  const runApplicantJobSearchRequest = useCallback(
    async (payload) => {
      const token = String(window.localStorage.getItem("jobuwin-auth-token") || "").trim();
      if (token && currentUser?.role === "applicant") {
        return postApplicantJobSearch(payload, token);
      }
      return postPublicJobSearch(payload);
    },
    [currentUser?.role]
  );

  const handleSearch = useCallback(async () => {
    pendingReturnFocusJobIdRef.current = null;
    try {
      window.sessionStorage.removeItem(JOB_SEARCH_RESTORE_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    keywordPillsRef.current?.flushDraftAndGetSerialized?.();
    lazyPhaseRef.current = 0;
    wasIntersectingRef.current = false;
    setApiSearchError("");

    const payload = buildApplicantJobSearchPayload(null, PAGE_SIZE);
    const msg = getSearchCriteriaValidationMessage(payload, useLiveJobSearch);
    if (msg) {
      setApiSearchError(msg);
      setApiSearchJobs([]);
      setApiSearchPage({ hasMore: false, nextCursor: null });
      const countryKeywordFail = !payload.countryCodes?.length || !payload.keywords?.length;
      setHasSearched(countryKeywordFail ? useLiveJobSearch : true);
      setVisibleCount(PAGE_SIZE);
      setShowEndMessage(false);
      setViewedJobIds([]);
      setTabResultIndex(0);
      const resolvedTitlesForUrl = keywordPillsRef.current?.flushDraftAndGetSerialized?.() ?? criteriaTitles;
      const urlParams = buildJobSearchParamsFromState({
        criteriaCountryCodes,
        criteriaTitles: resolvedTitlesForUrl,
        criteriaWorkMode,
        criteriaSalaryCurrency,
        salaryMin: criteriaSalaryMin,
        salaryMax: criteriaSalaryMax,
        showNotForMe,
        showApplied,
        showSaved,
        resultsView,
      });
      const qs = urlParams.toString();
      jobSearchUrlSyncedRef.current = qs;
      setSearchParams(urlParams, { replace: true });
      return;
    }

    setHasSearched(true);
    setVisibleCount(PAGE_SIZE);
    setShowEndMessage(false);
    setViewedJobIds([]);
    setTabResultIndex(0);

    if (!useLiveJobSearch) {
      setApiSearchError(JOB_SEARCH_REQUIRES_API_MESSAGE);
      setApiSearchJobs([]);
      setApiSearchPage({ hasMore: false, nextCursor: null });
      return;
    }

    setApiSearchJobs([]);
    setApiSearchPage({ hasMore: false, nextCursor: null });
    setApiSearchLoading(true);
    const mySeq = ++applicantSearchSeqRef.current;
    try {
      const mergeJobsById = (acc, incoming) => {
        const seen = new Set(acc.map((j) => j.id));
        const out = [...acc];
        for (const j of incoming || []) {
          if (!seen.has(j.id)) {
            seen.add(j.id);
            out.push(j);
          }
        }
        return out;
      };

      let accumulated = [];
      let cursor = null;
      let lastHasMore = false;
      let lastNextCursor = null;

      for (let pageIdx = 0; pageIdx < MAX_APPLICANT_JOB_SEARCH_AUTO_PAGES; pageIdx += 1) {
        if (mySeq !== applicantSearchSeqRef.current) return;
        const reqPayload =
          cursor == null ? payload : buildApplicantJobSearchPayload(cursor, PAGE_SIZE);
        let jobs;
        let page;
        try {
          const res = await runApplicantJobSearchRequest(reqPayload);
          jobs = res.jobs;
          page = res.page;
        } catch (pageErr) {
          if (pageIdx === 0) throw pageErr;
          lastHasMore = true;
          lastNextCursor = cursor;
          break;
        }
        if (mySeq !== applicantSearchSeqRef.current) return;
        accumulated = mergeJobsById(accumulated, jobs);
        lastHasMore = Boolean(page?.hasMore);
        lastNextCursor = page?.nextCursor || null;
        if (!lastHasMore || !lastNextCursor) break;
        cursor = lastNextCursor;
      }

      setApiSearchJobs(accumulated);
      setApiSearchPage({ hasMore: lastHasMore, nextCursor: lastNextCursor });
      const resolvedTitlesForUrl = keywordPillsRef.current?.flushDraftAndGetSerialized?.() ?? criteriaTitles;
      const urlParams = buildJobSearchParamsFromState({
        criteriaCountryCodes,
        criteriaTitles: resolvedTitlesForUrl,
        criteriaWorkMode,
        criteriaSalaryCurrency,
        salaryMin: criteriaSalaryMin,
        salaryMax: criteriaSalaryMax,
        showNotForMe,
        showApplied,
        showSaved,
        resultsView,
      });
      const qs = urlParams.toString();
      jobSearchUrlSyncedRef.current = qs;
      setSearchParams(urlParams, { replace: true });
    } catch (err) {
      setApiSearchError(err instanceof Error ? err.message : "Search failed.");
      setApiSearchJobs([]);
      setApiSearchPage({ hasMore: false, nextCursor: null });
    } finally {
      setApiSearchLoading(false);
    }
  }, [
    buildApplicantJobSearchPayload,
    criteriaCountryCodes,
    criteriaSalaryCurrency,
    criteriaSalaryMax,
    criteriaSalaryMin,
    criteriaTitles,
    criteriaWorkMode,
    currentUser,
    resultsView,
    runApplicantJobSearchRequest,
    setSearchParams,
    showApplied,
    showNotForMe,
    showSaved,
    useLiveJobSearch,
  ]);

  useEffect(() => {
    const ui = location.state?.searchUiState;
    if (!ui || typeof ui !== "object") {
      executeLastSearchMountKeyRef.current = null;
      return;
    }
    if (!ui.executeSearchOnMount || !useLiveJobSearch) {
      executeLastSearchMountKeyRef.current = null;
      return;
    }
    const key = [
      ...(Array.isArray(ui.criteriaCountryCodes) ? ui.criteriaCountryCodes : []),
      "|",
      String(ui.criteriaTitles || ""),
      "|",
      String(ui.criteriaWorkMode || ""),
      "|",
      String(ui.salaryMin ?? ""),
      "|",
      String(ui.salaryMax ?? ""),
      "|",
      String(ui.criteriaSalaryCurrency || ""),
    ].join("");
    if (executeLastSearchMountKeyRef.current === key) return;
    executeLastSearchMountKeyRef.current = key;
    const { executeSearchOnMount: _omit, ...restUi } = ui;
    navigate(".", { replace: true, state: { searchUiState: restUi } });
    void handleSearch();
  }, [location.state, navigate, handleSearch, useLiveJobSearch]);

  useLayoutEffect(() => {
    if (!useLiveJobSearch) return;
    if (location.state?.searchUiState?.executeSearchOnMount) return;

    const routerRestore = location.state?.searchUiState;
    if (isJobSearchRestoreUiState(routerRestore)) {
      const canonical = canonicalJobSearchQueryStringFromLocationSearch(location.search);
      if (canonical != null) jobSearchUrlSyncedRef.current = canonical;
      return;
    }

    if (navType === "POP") {
      const snapshot = readJobSearchRestoreSnapshot();
      if (snapshot) {
        const canonical = canonicalJobSearchQueryStringFromLocationSearch(location.search);
        if (canonical != null) jobSearchUrlSyncedRef.current = canonical;
        return;
      }
    }

    const canonical = canonicalJobSearchQueryStringFromLocationSearch(location.search);
    if (canonical == null) return;
    if (jobSearchUrlSyncedRef.current === canonical) return;
    if (urlAutoSearchInFlightRef.current) return;
    urlAutoSearchInFlightRef.current = true;
    queueMicrotask(async () => {
      try {
        await handleSearch();
      } finally {
        urlAutoSearchInFlightRef.current = false;
      }
    });
  }, [location.search, location.state, navType, useLiveJobSearch, handleSearch]);

  const fetchMoreApplicantJobs = useCallback(async () => {
    if (!useLiveJobSearch || !hasSearched || !apiSearchPage.hasMore || apiSearchLoading) return;
    const cursor = apiSearchPage.nextCursor;
    if (!cursor) return;
    const payload = buildApplicantJobSearchPayload(cursor, PAGE_SIZE);
    setApiSearchLoading(true);
    try {
      const { jobs, page } = await runApplicantJobSearchRequest(payload);
      setApiSearchJobs((prev) => {
        const seen = new Set(prev.map((j) => j.id));
        const merged = [...prev];
        for (const j of jobs || []) {
          if (!seen.has(j.id)) {
            seen.add(j.id);
            merged.push(j);
          }
        }
        return merged;
      });
      setApiSearchPage({ hasMore: Boolean(page?.hasMore), nextCursor: page?.nextCursor || null });
    } catch {
      /* keep existing rows; user can scroll again */
    } finally {
      setApiSearchLoading(false);
    }
  }, [
    apiSearchLoading,
    apiSearchPage.hasMore,
    apiSearchPage.nextCursor,
    buildApplicantJobSearchPayload,
    hasSearched,
    runApplicantJobSearchRequest,
    useLiveJobSearch,
  ]);

  const refreshSavedSearchCount = useCallback(async () => {
    const token = window.localStorage.getItem("jobuwin-auth-token");
    if (!currentUser || !isApiConfigured() || !String(token || "").trim()) {
      setSavedSearchCount(null);
      return;
    }
    setSavedSearchListLoading(true);
    try {
      const rows = await listApplicantSavedSearches(token);
      setSavedSearchCount(Array.isArray(rows) ? rows.length : 0);
    } catch {
      setSavedSearchCount(null);
    } finally {
      setSavedSearchListLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    void refreshSavedSearchCount();
  }, [refreshSavedSearchCount]);

  useEffect(() => {
    if (navType !== "PUSH") return;
    if (location.state?.searchUiState) return;
    try {
      window.sessionStorage.removeItem(JOB_SEARCH_RESTORE_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, [navType, location.state]);

  const handleResetCriteria = useCallback(() => {
    pendingReturnFocusJobIdRef.current = null;
    try {
      window.sessionStorage.removeItem(JOB_SEARCH_RESTORE_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setCriteriaCountryCodes([]);
    setCriteriaCountryInputDraft("");
    setCriteriaTitles("");
    setCriteriaWorkMode("");
    setCriteriaSalaryCurrency(residenceIso ? defaultCurrencyFromResidenceIso(residenceIso) : "GBP");
    setCriteriaSalaryMin("");
    setCriteriaSalaryMax("");
    criteriaFormRef.current?.reset?.();
    keywordPillsRef.current?.clearComposeDraft?.();

    lazyPhaseRef.current = 0;
    wasIntersectingRef.current = false;
    setHasSearched(false);
    setVisibleCount(PAGE_SIZE);
    setShowEndMessage(false);
    setViewedJobIds([]);
    setTabResultIndex(0);
    setShowNotForMe(true);
    setShowApplied(true);
    setShowSaved(true);
    setApiSearchJobs([]);
    setApiSearchPage({ hasMore: false, nextCursor: null });
    setApiSearchLoading(false);
    setApiSearchError("");
    jobSearchUrlSyncedRef.current = "";
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [residenceIso, setCriteriaSalaryCurrency, setSearchParams]);

  useEffect(() => {
    if (!useLiveJobSearch || !hasSearched) return;
    const payload = buildApplicantJobSearchPayload(null, PAGE_SIZE);
    if (getSearchCriteriaValidationMessage(payload, useLiveJobSearch)) return;
    const resolvedTitles = keywordPillsRef.current?.flushDraftAndGetSerialized?.() ?? criteriaTitles;
    const p = buildJobSearchParamsFromState({
      criteriaCountryCodes,
      criteriaTitles: resolvedTitles,
      criteriaWorkMode,
      criteriaSalaryCurrency,
      salaryMin: criteriaSalaryMin,
      salaryMax: criteriaSalaryMax,
      showNotForMe,
      showApplied,
      showSaved,
      resultsView,
    });
    const qs = p.toString();
    if (jobSearchUrlSyncedRef.current === qs) return;
    jobSearchUrlSyncedRef.current = qs;
    setSearchParams(p, { replace: true });
  }, [
    hasSearched,
    useLiveJobSearch,
    showNotForMe,
    showApplied,
    showSaved,
    resultsView,
    criteriaCountryCodes,
    criteriaTitles,
    criteriaWorkMode,
    criteriaSalaryCurrency,
    criteriaSalaryMin,
    criteriaSalaryMax,
    setSearchParams,
    buildApplicantJobSearchPayload,
  ]);

  const handleOpenSaveSearchModal = useCallback(async () => {
    keywordPillsRef.current?.flushDraftAndGetSerialized?.();
    setSaveSearchError("");
    setSaveSearchName("");
    const payload = buildApplicantJobSearchPayload(null, PAGE_SIZE);
    const msg = getSearchCriteriaValidationMessage(payload, useLiveJobSearch);
    if (msg) {
      setApiSearchError(msg);
      setIsPanelVisible(true);
      return;
    }
    if (currentUser && isApiConfigured()) {
      await refreshSavedSearchCount();
    }
    setShowSaveSearchModal(true);
  }, [buildApplicantJobSearchPayload, currentUser, refreshSavedSearchCount, useLiveJobSearch]);

  const handleCloseSaveSearchModal = useCallback(() => {
    if (saveSearchBusy) return;
    setShowSaveSearchModal(false);
    setSaveSearchError("");
  }, [saveSearchBusy]);

  const handleConfirmSaveSearch = useCallback(async () => {
    if (saveSearchBusy) return;
    if (savedSearchLimitReached) return;
    keywordPillsRef.current?.flushDraftAndGetSerialized?.();
    const payload = buildApplicantJobSearchPayload(null, PAGE_SIZE);
    const criteriaMsg = getSearchCriteriaValidationMessage(payload, useLiveJobSearch);
    if (criteriaMsg) {
      setIsPanelVisible(true);
      setShowSaveSearchModal(false);
      setApiSearchError(criteriaMsg);
      return;
    }
    const name = String(saveSearchName || "").trim();
    if (!name) {
      setSaveSearchError("Give a memorable name for this search.");
      return;
    }
    setSaveSearchError("");
    setSaveSearchBusy(true);
    try {
      const resolvedTitles = keywordPillsRef.current?.flushDraftAndGetSerialized?.() ?? criteriaTitles;
      const criteria = {
        criteriaCountryCodes,
        criteriaTitles: resolvedTitles,
        criteriaWorkMode,
        criteriaSalaryCurrency,
        salaryMin: String(criteriaSalaryMin ?? "").trim(),
        salaryMax: String(criteriaSalaryMax ?? "").trim(),
        showNotForMe,
        showApplied,
        showSaved,
      };
      const token = window.localStorage.getItem("jobuwin-auth-token");
      await postApplicantSavedSearch({ name, criteria }, token);
      setShowSaveSearchModal(false);
      setSaveSearchName("");
      await refreshSavedSearchCount();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not save search.";
      setSaveSearchError(msg);
      if (err instanceof Error && err.code === "SAVED_SEARCH_LIMIT_REACHED") {
        await refreshSavedSearchCount();
      }
    } finally {
      setSaveSearchBusy(false);
    }
  }, [
    criteriaCountryCodes,
    criteriaSalaryCurrency,
    criteriaSalaryMax,
    criteriaSalaryMin,
    criteriaTitles,
    criteriaWorkMode,
    showNotForMe,
    showApplied,
    showSaved,
    buildApplicantJobSearchPayload,
    refreshSavedSearchCount,
    saveSearchBusy,
    saveSearchName,
    savedSearchLimitReached,
    useLiveJobSearch,
  ]);

  const addCriteriaCountryGroup = useCallback((groupCodes) => {
    const list = [
      ...new Set((groupCodes || []).map((c) => String(c || "").trim().toUpperCase()).filter(Boolean)),
    ];
    if (!list.length) return;
    setCriteriaCountryCodes((prev) => {
      const seen = new Set((prev || []).map((c) => String(c || "").trim().toUpperCase()).filter(Boolean));
      const out = [...(prev || [])];
      for (const c of list) {
        if (seen.has(c)) continue;
        if (out.length >= MAX_COUNTRY_CODES) break;
        seen.add(c);
        out.push(c);
      }
      return out;
    });
    setCriteriaCountryInputDraft("");
  }, []);

  const refetchJobPostInteractionsForIds = useCallback(async (jobIds) => {
    const token = String(window.localStorage.getItem("jobuwin-auth-token") || "").trim();
    if (!token || currentUser?.role !== "applicant") return;
    const list = [...new Set((jobIds || []).map(String))].filter((id) => JOB_POST_ID_UUID_RE.test(id));
    if (!list.length) return;
    try {
      const merged = {};
      for (let i = 0; i < list.length; i += INTERACTION_STATUS_CHUNK) {
        const chunk = list.slice(i, i + INTERACTION_STATUS_CHUNK);
        const by = await postApplicantJobPostInteractionStatus(chunk, token);
        Object.assign(merged, normalizeByJobPostIdFromApi(by));
      }
      setJobPostInteractionById((prev) => {
        const next = { ...prev };
        for (const id of list) {
          next[id] = merged[id] || emptyJobPostInteraction();
        }
        return next;
      });
    } catch {
      /* keep prior UI */
    }
  }, [currentUser?.role]);

  const toggleSaveLater = useCallback(
    async (jobId) => {
      const id = String(jobId || "").trim();
      if (!JOB_POST_ID_UUID_RE.test(id) || currentUser?.role !== "applicant") return;
      const token = String(window.localStorage.getItem("jobuwin-auth-token") || "").trim();
      if (!token) return;
      const snap = jobPostInteractionByIdRef.current[id] || emptyJobPostInteraction();
      const willTurnOn = !snap.saved;
      setJobPostInteractionById((prev) => {
        const cur = prev[id] || emptyJobPostInteraction();
        return {
          ...prev,
          [id]: {
            ...cur,
            saved: willTurnOn,
            notForMe: willTurnOn ? false : cur.notForMe,
            savedAtIso: willTurnOn ? new Date().toISOString() : "",
            notForMeAtIso: willTurnOn ? "" : cur.notForMeAtIso,
          },
        };
      });
      try {
        if (willTurnOn) await putApplicantJobPostSaved(id, token);
        else await deleteApplicantJobPostSaved(id, token);
      } catch {
        await refetchJobPostInteractionsForIds([id]);
      }
    },
    [currentUser?.role, refetchJobPostInteractionsForIds]
  );

  const toggleNotForMe = useCallback(
    async (jobId) => {
      const id = String(jobId || "").trim();
      if (!JOB_POST_ID_UUID_RE.test(id) || currentUser?.role !== "applicant") return;
      const token = String(window.localStorage.getItem("jobuwin-auth-token") || "").trim();
      if (!token) return;
      const snap = jobPostInteractionByIdRef.current[id] || emptyJobPostInteraction();
      const willTurnOn = !snap.notForMe;
      setJobPostInteractionById((prev) => {
        const cur = prev[id] || emptyJobPostInteraction();
        return {
          ...prev,
          [id]: {
            ...cur,
            notForMe: willTurnOn,
            saved: willTurnOn ? false : cur.saved,
            notForMeAtIso: willTurnOn ? new Date().toISOString() : "",
            savedAtIso: willTurnOn ? "" : cur.savedAtIso,
          },
        };
      });
      try {
        if (willTurnOn) await putApplicantJobPostNotForMe(id, token);
        else await deleteApplicantJobPostNotForMe(id, token);
      } catch {
        await refetchJobPostInteractionsForIds([id]);
      }
    },
    [currentUser?.role, refetchJobPostInteractionsForIds]
  );

  const openJobDetails = useCallback(
    (job) => {
      const jobId = String(job?.id || "").trim();
      if (!jobId) return;
      const nextViewedJobIds = viewedJobIds.includes(jobId)
        ? viewedJobIds
        : [...viewedJobIds, jobId];

      setViewedJobIds(nextViewedJobIds);

      const scrollY = typeof window !== "undefined" ? window.scrollY : 0;
      const searchUiState = {
        hasSearched: true,
        visibleCount,
        showEndMessage,
        viewedJobIds: nextViewedJobIds,
        showNotForMe,
        showApplied,
        showSaved,
        criteriaCountryCodes,
        criteriaWorkMode,
        criteriaSalaryCurrency,
        criteriaTitles,
        salaryMin: String(criteriaSalaryMin ?? "").trim(),
        salaryMax: String(criteriaSalaryMax ?? "").trim(),
        resultsView,
        tabResultIndex,
        lazyPhase: lazyPhaseRef.current,
        scrollY,
        lastOpenedJobId: String(jobId),
      };
      if (useLiveJobSearch) {
        searchUiState.apiSearchJobs = apiSearchJobs;
        searchUiState.apiSearchHasMore = apiSearchPage.hasMore;
        searchUiState.apiSearchNextCursor = apiSearchPage.nextCursor;
      }

      try {
        window.sessionStorage.setItem(JOB_SEARCH_RESTORE_STORAGE_KEY, JSON.stringify(searchUiState));
      } catch {
        /* quota / private mode */
      }

      navigate(jobPublicHref(job), {
        state: {
          fromPath: location.pathname,
          fromLabel: SEARCH_RESULTS_LABEL,
          returnState: {
            searchUiState,
          },
        },
      });
    },
    [
      apiSearchJobs,
      apiSearchPage.hasMore,
      apiSearchPage.nextCursor,
      criteriaCountryCodes,
      criteriaSalaryCurrency,
      criteriaSalaryMax,
      criteriaSalaryMin,
      criteriaTitles,
      criteriaWorkMode,
      location.pathname,
      navigate,
      resultsView,
      showApplied,
      showEndMessage,
      showNotForMe,
      showSaved,
      tabResultIndex,
      useLiveJobSearch,
      viewedJobIds,
      visibleCount,
    ]
  );

  const statusByJobId = useMemo(() => {
    const map = {};
    if (!useLiveJobSearch || !hasSearched) return map;
    for (const job of apiSearchJobs) {
      const id = job.id;
      const intr = jobPostInteractionById[id] || emptyJobPostInteraction();
      if (intr.applied) map[id] = RESULT_STATUS_OPTIONS[1];
      else if (intr.notForMe) map[id] = RESULT_STATUS_OPTIONS[2];
      else if (intr.saved) map[id] = RESULT_STATUS_OPTIONS[3];
    }
    return map;
  }, [apiSearchJobs, hasSearched, jobPostInteractionById, useLiveJobSearch]);

  const filteredJobs = useMemo(() => {
    if (!useLiveJobSearch || !hasSearched) return [];
    return apiSearchJobs.filter((job) => {
      const status = statusByJobId[job.id]?.key;
      if (status === "not-for-me" && !showNotForMe) return false;
      if (status === "applied" && !showApplied) return false;
      if (status === "saved" && !showSaved) return false;
      return true;
    });
  }, [apiSearchJobs, hasSearched, showApplied, showNotForMe, showSaved, statusByJobId, useLiveJobSearch]);

  useEffect(() => {
    if (!hasSearched || !useLiveJobSearch) return;
    setVisibleCount(filteredJobs.length);
  }, [filteredJobs.length, hasSearched, useLiveJobSearch]);

  useEffect(() => {
    if (!hasSearched || !useLiveJobSearch) return;
    if (!apiSearchPage.hasMore && !apiSearchLoading && filteredJobs.length > 0) {
      setShowEndMessage(true);
    }
  }, [
    apiSearchLoading,
    apiSearchPage.hasMore,
    filteredJobs.length,
    hasSearched,
    useLiveJobSearch,
  ]);

  useEffect(() => {
    if (!hasSearched) return;
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const now = entry.isIntersecting;
        const crossedInto = now && !wasIntersectingRef.current;
        wasIntersectingRef.current = now;

        if (!crossedInto) return;

        if (useLiveJobSearch) {
          if (apiSearchPage.hasMore && !apiSearchLoading) {
            void fetchMoreApplicantJobs();
            return;
          }
          if (lazyPhaseRef.current >= 2) return;
          if (lazyPhaseRef.current === 0) {
            lazyPhaseRef.current = 1;
            setVisibleCount(Math.min(filteredJobs.length, PAGE_SIZE * 2));
            return;
          }
          if (lazyPhaseRef.current === 1) {
            lazyPhaseRef.current = 2;
            setShowEndMessage(true);
          }
          return;
        }
      },
      { root: null, rootMargin: "0px 0px 140px 0px", threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [
    apiSearchLoading,
    apiSearchPage.hasMore,
    fetchMoreApplicantJobs,
    filteredJobs.length,
    hasSearched,
    useLiveJobSearch,
  ]);

  useEffect(() => {
    if (!hasSearched || useLiveJobSearch) return;
    setVisibleCount((prev) => Math.min(prev, filteredJobs.length));
  }, [filteredJobs.length, hasSearched, useLiveJobSearch]);

  useEffect(() => {
    setTabResultIndex((prev) => {
      if (filteredJobs.length === 0) return 0;
      return Math.min(prev, filteredJobs.length - 1);
    });
  }, [filteredJobs.length]);

  const visibleJobs = filteredJobs.slice(0, visibleCount);
  const tabViewJob = filteredJobs[tabResultIndex] || null;
  const tabViewStatus = tabViewJob ? statusByJobId[tabViewJob.id] : null;

  useEffect(() => {
    const id = tabViewJob?.id ? String(tabViewJob.id) : "";
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
    if (!id || !isUuid || currentUser?.role !== "applicant" || !useLiveJobSearch) {
      setTabApplicationPitch({ jobId: null, url: "", loaded: false });
      setTabApplicationPitchDeleteError("");
      return undefined;
    }
    const token = String(window.localStorage.getItem("jobuwin-auth-token") || "").trim();
    if (!token) {
      setTabApplicationPitch({ jobId: null, url: "", loaded: false });
      return undefined;
    }
    let cancelled = false;
    setTabApplicationPitch({ jobId: id, url: "", loaded: false });
    setTabApplicationPitchDeleteError("");
    (async () => {
      try {
        const { application } = await getApplicantMyApplicationForJobPost(id, token);
        if (cancelled) return;
        setTabApplicationPitch({
          jobId: id,
          url: application?.pitchVideoUrl ? String(application.pitchVideoUrl).trim() : "",
          loaded: true,
        });
      } catch {
        if (!cancelled) {
          setTabApplicationPitch({ jobId: id, url: "", loaded: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tabViewJob?.id, currentUser?.role, useLiveJobSearch]);

  useLayoutEffect(() => {
    const restoreUi = resolveJobSearchRestoreUi(location, navType);
    const restoreScrollY = jobSearchRestoreScrollY(restoreUi);
    if (restoreScrollY === null) return;
    if (scrollRestoredForLocationKeyRef.current === location.key) return;
    if (!hasSearched) return;
    if (useLiveJobSearch && filteredJobs.length === 0) return;

    scrollRestoredForLocationKeyRef.current = location.key;

    const applyScroll = () => {
      window.scrollTo({ top: restoreScrollY, left: 0, behavior: "auto" });
    };
    applyScroll();
    requestAnimationFrame(() => {
      applyScroll();
      requestAnimationFrame(applyScroll);
    });
  }, [
    filteredJobs.length,
    hasSearched,
    location.key,
    location.state,
    navType,
    useLiveJobSearch,
    visibleCount,
  ]);

  useLayoutEffect(() => {
    const restoreUi = resolveJobSearchRestoreUi(location, navType);
    if (restoreUi && typeof restoreUi.lastOpenedJobId === "string" && restoreUi.lastOpenedJobId.trim()) {
      pendingReturnFocusJobIdRef.current = restoreUi.lastOpenedJobId.trim();
    }

    const id = pendingReturnFocusJobIdRef.current;
    if (!id || !hasSearched) return;
    if (useLiveJobSearch && filteredJobs.length === 0) return;

    const idx = filteredJobs.findIndex((j) => String(j.id) === String(id));
    if (idx < 0) {
      if (filteredJobs.length > 0) {
        pendingReturnFocusJobIdRef.current = null;
      }
      return;
    }

    setTabResultIndex(idx);
    setVisibleCount((v) => Math.max(v, idx + 1));

    if (resultsView !== "list") {
      pendingReturnFocusJobIdRef.current = null;
    }
  }, [filteredJobs, hasSearched, location.key, location.state, navType, resultsView, useLiveJobSearch]);

  useLayoutEffect(() => {
    const restoreUi = resolveJobSearchRestoreUi(location, navType);
    if (jobSearchRestoreScrollY(restoreUi) !== null) {
      pendingReturnFocusJobIdRef.current = null;
      return;
    }

    const id = pendingReturnFocusJobIdRef.current;
    if (!id || !hasSearched || resultsView !== "list") return;
    if (useLiveJobSearch && filteredJobs.length === 0) return;

    const idx = filteredJobs.findIndex((j) => String(j.id) === String(id));
    if (idx < 0 || visibleCount <= idx) return;

    pendingReturnFocusJobIdRef.current = null;

    const attr =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(id)
        : String(id).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    requestAnimationFrame(() => {
      document
        .querySelector(`[data-job-search-result-id="${attr}"]`)
        ?.scrollIntoView({ block: "center", behavior: "auto" });
    });
  }, [
    filteredJobs,
    hasSearched,
    location.key,
    location.state,
    navType,
    resultsView,
    useLiveJobSearch,
    visibleCount,
  ]);

  const goToPrevTabResult = useCallback(() => {
    setTabResultIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const goToNextTabResult = useCallback(() => {
    setTabResultIndex((prev) => Math.min(filteredJobs.length - 1, prev + 1));
  }, [filteredJobs.length]);

  const handleDeleteTabApplicationPitch = useCallback(async () => {
    const id = tabViewJob?.id ? String(tabViewJob.id) : "";
    if (!id || !useLiveJobSearch) return;
    const token = String(window.localStorage.getItem("jobuwin-auth-token") || "").trim();
    if (!token) return;
    setTabApplicationPitchDeleteError("");
    setTabApplicationPitchDeleteBusy(true);
    try {
      await deleteApplicantApplicationPitchVideo(id, token);
      setTabApplicationPitch((prev) =>
        String(prev.jobId) === String(id) ? { jobId: id, url: "", loaded: true } : prev
      );
    } catch (e) {
      setTabApplicationPitchDeleteError(
        e instanceof Error ? e.message : "Could not remove your saved pitch."
      );
    } finally {
      setTabApplicationPitchDeleteBusy(false);
    }
  }, [tabViewJob?.id, useLiveJobSearch]);

  const onTabTouchStart = useCallback((event) => {
    tabTouchStartXRef.current = event.changedTouches[0]?.clientX ?? null;
  }, []);

  const onTabTouchEnd = useCallback(
    (event) => {
      const startX = tabTouchStartXRef.current;
      const endX = event.changedTouches[0]?.clientX ?? null;
      tabTouchStartXRef.current = null;

      if (startX === null || endX === null) return;
      const delta = startX - endX;
      const swipeThreshold = 40;

      if (delta > swipeThreshold) {
        goToNextTabResult();
        return;
      }

      if (delta < -swipeThreshold) {
        goToPrevTabResult();
      }
    },
    [goToNextTabResult, goToPrevTabResult]
  );

  const closeApplyHelp = useCallback(() => {
    setShowApplyHelp(false);
    const video = applyHelpVideoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  }, []);

  useEffect(() => {
    if (!showApplyHelp) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeApplyHelp();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeApplyHelp, showApplyHelp]);

  const closeApplyShineModal = useCallback(() => {
    setShowApplyShineModal(false);
    setApplyShineTargetJobId(null);
  }, []);

  const handleApplyPitchModalSubmit = useCallback(
    async ({ blob, mime }) => {
      const token = String(window.localStorage.getItem("jobuwin-auth-token") || "").trim();
      if (!isApiConfigured() || !token) {
        throw new Error(
          !isApiConfigured()
            ? "Submitting your application requires the API to be configured."
            : "Sign in to submit your application pitch."
        );
      }
      const jobId = applyShineTargetJobId;
      if (!jobId) {
        throw new Error("No job selected.");
      }
      const { pitchVideoUrl } = await postApplicantApplicationPitchVideo(String(jobId), blob, mime, token);
      captureAnalyticsEvent("application_submitted", {
        job_post_id: String(jobId),
      });
      const appliedNow = new Date();
      const appliedAtIso = appliedNow.toISOString();
      const appliedOn = appliedNow.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
      setJobPostInteractionById((prev) => ({
        ...prev,
        [jobId]: {
          notForMe: false,
          saved: false,
          applied: true,
          appliedOn,
          appliedAtIso,
          savedAtIso: "",
          notForMeAtIso: "",
        },
      }));
      const submittedId = String(jobId);
      setTabApplicationPitch((prev) =>
        String(prev.jobId) === submittedId
          ? {
              jobId: submittedId,
              url: pitchVideoUrl ? String(pitchVideoUrl).trim() : "",
              loaded: true,
            }
          : prev
      );
    },
    [applyShineTargetJobId]
  );

  return (
    <ApplicantShell currentUser={currentUser} setCurrentUser={setCurrentUser} authToken={authToken}>
      <PageSeo
        title="Find jobs"
        description="Search genuine job openings on JOBUWIN. Filter by country, salary, work mode, and keywords to find roles that fit you."
        canonicalPath="/job-search"
        schemaType="website"
        noindex={jobSearchUrlHasAnyCriteria(parseJobSearchUrlFragment(location.search))}
      />
      <main className="nd1-shell nd1-applicant-main nd1-applicant-job-search-page applicant-home job-search-page">
        <header className="nd1-applicant-job-search-page__intro">
          {currentUser?.role === "applicant" ? (
            <SiteBreadcrumbs
              className="nd1-applicant-breadcrumbs"
              items={[
                { name: "Applicant home", path: "/applicant-home" },
                { name: "Job search", path: "/job-search", current: true },
              ]}
            />
          ) : (
            <SiteBreadcrumbs
              className="nd1-applicant-breadcrumbs"
              items={[
                { name: "Home", path: "/" },
                { name: "Job search", path: "/job-search", current: true },
              ]}
            />
          )}
          <h1 className="nd1-applicant-job-search-page__title">Find your next opportunity</h1>
          <p className="nd1-applicant-meta">
            Build your search criteria and press Search to find matching jobs.
          </p>
        </header>

          {isApiConfigured() ? (
            <>
              <section className="nd1-applicant-section">
                <div className="nd1-applicant-section-card">
              <section className="job-search-panel">
                <div className="nd1-applicant-section-head job-search-panel-head">
                  <div className="nd1-applicant-section-head__title-wrap">
                    <span className="nd1-applicant-section-head__icon-wrap" aria-hidden="true">
                      <SlidersHorizontal size={20} strokeWidth={1.75} />
                    </span>
                    <h2 className="nd1-applicant-section-head__title job-search-title">Search criteria</h2>
                  </div>
                </div>

                  <form
                    className="job-search-form"
                    id="jobSearchCriteriaPanel"
                    ref={criteriaFormRef}
                    onSubmit={(event) => event.preventDefault()}
                  >
                    <div className="job-search-field-group">
                      <label htmlFor="searchCountry">Countries</label>
                      <WorkCountriesPillPicker
                        disabled={false}
                        countryCodes={criteriaCountryCodes}
                        inputDraft={criteriaCountryInputDraft}
                        onDraftChange={setCriteriaCountryInputDraft}
                        onAddCode={(iso) => {
                          const c = String(iso || "").trim().toUpperCase();
                          if (!c) return;
                          setCriteriaCountryCodes((prev) => {
                            const list = Array.isArray(prev) ? prev : [];
                            if (list.includes(c)) return list;
                            if (list.length >= MAX_COUNTRY_CODES) return list;
                            return [...list, c];
                          });
                          setCriteriaCountryInputDraft("");
                        }}
                        onRemoveCode={(iso) => {
                          const c = String(iso || "").trim().toUpperCase();
                          setCriteriaCountryCodes((prev) =>
                            Array.isArray(prev) ? prev.filter((x) => x !== c) : []
                          );
                        }}
                        maxCodes={MAX_COUNTRY_CODES}
                        inputId="searchCountry"
                        placeholder="Search countries"
                        placeholderDisabled="Search countries"
                      />
                      {EEA_COUNTRY_GROUP ? (
                        <div
                          className="profile-manage-country-group-shortcuts job-search-country-group-shortcuts"
                          role="group"
                          aria-label="Country shortcuts"
                        >
                          <button
                            type="button"
                            className="profile-manage-country-group-shortcut-link"
                            title={`Add: ${EEA_COUNTRY_GROUP.countryCodes.join(", ")}`}
                            onClick={() => addCriteriaCountryGroup(EEA_COUNTRY_GROUP.countryCodes)}
                          >
                            <span className="profile-manage-country-group-shortcut-link__plus" aria-hidden="true">
                              +
                            </span>
                            <span className="profile-manage-country-group-shortcut-link__label">
                              {EEA_COUNTRY_GROUP.label}
                            </span>
                          </button>
                        </div>
                      ) : null}
                    </div>

                    <div className="job-search-field-group">
                      <label htmlFor="searchTitles">Keywords</label>
                      <KeywordPillsInput
                        ref={keywordPillsRef}
                        composeId="searchTitles"
                        value={criteriaTitles}
                        onChange={(event) => setCriteriaTitles(event.target.value)}
                        placeholder="Add job title or keyword"
                        ariaLabel="Add job title or keyword"
                      />
                    </div>

                    {currentUser?.role === "applicant" ? (
                    <div className="job-search-field-group job-search-field-group--pills">
                      <PillRadioFieldset
                        legend={`Salary range (${String(criteriaSalaryCurrency || "GBP").toUpperCase()})`}
                        name="jobSearchSalaryCurrency"
                        value={criteriaSalaryCurrency}
                        onChange={(next) => setCriteriaSalaryCurrency(next)}
                        options={JOB_SEARCH_SALARY_CURRENCY_PILL_OPTIONS}
                      />
                    </div>
                  ) : (
                    <div className="job-search-field-group">
                      <label htmlFor="job-search-salary-currency">Salary currency</label>
                      <select
                        id="job-search-salary-currency"
                        name="salaryCurrency"
                        value={criteriaSalaryCurrency}
                        onChange={(event) => setCriteriaSalaryCurrency(event.target.value)}
                      >
                        {SALARY_CURRENCY_OPTIONS.map(({ value, symbol, name }) => (
                          <option key={value} value={value}>
                            {symbol} {value} — {name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                    <div className="job-search-range-row">
                      <div className="job-search-field-group">
                        <label htmlFor="salaryMin">
                          Salary minimum ({String(criteriaSalaryCurrency || "GBP").toUpperCase()})
                        </label>
                        <div className="job-create-salary-input-wrap">
                          {hasSalaryCurrency(criteriaSalaryCurrency) ? (
                            <span className="job-create-salary-prefix" aria-hidden="true">
                              <SalaryCurrencyIcon currency={criteriaSalaryCurrency} size={18} />
                              <span className="job-create-salary-acronym">
                                {String(criteriaSalaryCurrency).trim().toUpperCase()}
                              </span>
                            </span>
                          ) : null}
                          <input
                            id="salaryMin"
                            name="salaryMin"
                            type="number"
                            min="0"
                            max="1000000"
                            placeholder="0"
                            value={criteriaSalaryMin}
                            onChange={(event) => setCriteriaSalaryMin(event.target.value)}
                          />
                        </div>
                      </div>
                      <div className="job-search-field-group">
                        <label htmlFor="salaryMax">
                          Salary maximum ({String(criteriaSalaryCurrency || "GBP").toUpperCase()})
                        </label>
                        <div className="job-create-salary-input-wrap">
                          {hasSalaryCurrency(criteriaSalaryCurrency) ? (
                            <span className="job-create-salary-prefix" aria-hidden="true">
                              <SalaryCurrencyIcon currency={criteriaSalaryCurrency} size={18} />
                              <span className="job-create-salary-acronym">
                                {String(criteriaSalaryCurrency).trim().toUpperCase()}
                              </span>
                            </span>
                          ) : null}
                          <input
                            id="salaryMax"
                            name="salaryMax"
                            type="number"
                            min="0"
                            max="1000000"
                            placeholder="1,000,000"
                            value={criteriaSalaryMax}
                            onChange={(event) => setCriteriaSalaryMax(event.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    {currentUser?.role === "applicant" ? (
                    <div className="job-search-field-group job-search-field-group--pills">
                      <PillRadioFieldset
                        legend="Work mode"
                        name="jobSearchWorkMode"
                        value={criteriaWorkMode}
                        onChange={(next) => setCriteriaWorkMode(next)}
                        options={JOB_SEARCH_WORK_MODE_PILL_OPTIONS}
                      />
                    </div>
                  ) : (
                    <div className="job-search-field-group">
                      <label htmlFor="job-search-work-mode">Work mode</label>
                      <select
                        id="job-search-work-mode"
                        name="workMode"
                        value={criteriaWorkMode}
                        onChange={(event) => setCriteriaWorkMode(event.target.value)}
                      >
                        <option value="">Any: Remote, Hybrid or Onsite</option>
                        <option value="remote">Remote only</option>
                        <option value="hybrid">Hybrid only</option>
                        <option value="onsite">Onsite only</option>
                      </select>
                    </div>
                  )}

                    {apiSearchError &&
                    (useLiveJobSearch || apiSearchError === JOB_SEARCH_COUNTRY_AND_KEYWORD_HINT) ? (
                      <p className="job-search-actions-error" role="alert">
                        {apiSearchError === JOB_SEARCH_COUNTRY_AND_KEYWORD_HINT ? (
                          <strong>{apiSearchError}</strong>
                        ) : (
                          apiSearchError
                        )}
                      </p>
                    ) : null}

                    <div className="job-search-actions">
                      {isApplicantJobSearch ? (
                        <button
                          type="button"
                          className="nd1-btn nd1-btn--outline nd1-applicant-btn-sm job-search-save-button"
                          aria-label="Save search"
                          title={
                            savedSearchLimitReached
                              ? "You have 8 saved searches. Delete one on your home page to save another."
                              : "Save search"
                          }
                          onClick={() => void handleOpenSaveSearchModal()}
                          disabled={savedSearchLimitReached}
                        >
                          <GuideIcon name="save" className="job-search-save-icon" />
                          Save search
                        </button>
                      ) : null}
                      <div className="job-search-primary-actions">
                        <button
                          type="button"
                          className="nd1-btn nd1-btn--outline nd1-applicant-btn-sm job-search-reset-button"
                          onClick={handleResetCriteria}
                        >
                          Reset
                        </button>
                        <button
                          type="button"
                          className="nd1-btn nd1-btn--primary search-submit-button"
                          onClick={handleSearch}
                        >
                          Search jobs
                        </button>
                      </div>
                    </div>
                  </form>
              </section>
                </div>
              </section>

              {hasSearched ? (
                <>
                  <section className="nd1-applicant-section nd1-applicant-job-search-results-section job-search-results">
                    <div className="nd1-applicant-section-head nd1-applicant-job-search-results-head">
                      <div className="nd1-applicant-section-head__title-wrap">
                        <span className="nd1-applicant-section-head__icon-wrap" aria-hidden="true">
                          <Briefcase size={20} strokeWidth={1.75} />
                        </span>
                        <h2 className="nd1-applicant-section-head__title">
                          {filteredJobs.length} jobs found
                        </h2>
                      </div>

                      <div
                        className="job-search-filter-options job-search-show-checkboxes"
                        aria-label="Which result statuses to show"
                      >
                        {isApplicantJobSearch ? (
                          <>
                        <span className="job-search-show-inline-heading">Show:</span>
                        <label className="job-search-show-label job-search-show-label--not-for-me">
                          <input
                            type="checkbox"
                            checked={showNotForMe}
                            onChange={(event) => setShowNotForMe(event.target.checked)}
                          />
                          <GuideIcon name="not-for-me" className="job-search-show-icon" />
                          Not for me
                        </label>
                        <label className="job-search-show-label job-search-show-label--applied">
                          <input
                            type="checkbox"
                            checked={showApplied}
                            onChange={(event) => setShowApplied(event.target.checked)}
                          />
                          <GuideIcon name="applied" className="job-search-show-icon" />
                          Applied
                        </label>
                        <label className="job-search-show-label job-search-show-label--saved">
                          <input
                            type="checkbox"
                            checked={showSaved}
                            onChange={(event) => setShowSaved(event.target.checked)}
                          />
                          <GuideIcon name="saved" className="job-search-show-icon" />
                          Saved
                        </label>
                          </>
                        ) : null}
                      </div>
                    </div>

                    {useLiveJobSearch && apiSearchLoading ? (
                      <p className="nd1-applicant-meta" aria-live="polite">
                        Loading results…
                      </p>
                    ) : null}

                    {resultsView === "list" ? (
                      <>
                        <div className="nd1-applicant-job-search-results">
                          {visibleJobs.map((job, index) => (
                            <ApplicantJobSearchResultCard
                              key={job.id}
                              job={job}
                              index={index}
                              onOpen={() => openJobDetails(job)}
                              statusRow={
                                statusByJobId[job.id] ? (
                                  <JobSearchInteractionStatusRow
                                    status={statusByJobId[job.id]}
                                    interaction={jobPostInteractionById[job.id]}
                                  />
                                ) : null
                              }
                            />
                          ))}
                        </div>

                        {visibleJobs.length === 0 ? (
                          <p className="nd1-applicant-meta job-search-end">No results for the current filters.</p>
                        ) : null}
                      </>
                    ) : (
                      <div className="nd1-applicant-job-search-tab-view" aria-label="Tab style job results">
                        {tabViewJob ? (
                          <>
                            <div
                              className="nd1-applicant-job-search-tab-nav"
                              role="toolbar"
                              aria-orientation="horizontal"
                              aria-label="Browse job results"
                            >
                              <button
                                type="button"
                                className="nd1-applicant-job-search-tab-nav-btn"
                                aria-label="Previous job"
                                onClick={goToPrevTabResult}
                                disabled={tabResultIndex === 0}
                              >
                                <GuideIcon name="chevron-left" />
                              </button>
                              <span className="nd1-applicant-job-search-tab-count">
                                <span
                                  className="nd1-applicant-job-search-result__index"
                                  aria-label={`Result ${tabResultIndex + 1}`}
                                >
                                  {tabResultIndex + 1}
                                </span>
                                {tabResultIndex + 1} / {filteredJobs.length}
                              </span>
                              <button
                                type="button"
                                className="nd1-applicant-job-search-tab-nav-btn"
                                aria-label="Next job"
                                onClick={goToNextTabResult}
                                disabled={tabResultIndex >= filteredJobs.length - 1}
                              >
                                <GuideIcon name="chevron-right" />
                              </button>
                            </div>

                            <div
                              className="nd1-applicant-job-search-tab-carousel"
                              onTouchStart={onTabTouchStart}
                              onTouchEnd={onTabTouchEnd}
                            >
                              <article className="nd1-applicant-job-search-tab-card">
                                <div className="job-tab-detail-head">
                                  <div className="job-tab-detail-head-main">
                                    <p className="job-company">{tabViewJob.company}</p>
                                    {tabViewStatus ? (
                                      <JobSearchInteractionStatusRow
                                        status={tabViewStatus}
                                        interaction={jobPostInteractionById[tabViewJob.id]}
                                      />
                                    ) : null}
                                    <h3>{tabViewJob.title}</h3>
                                    {tabViewJob.deadline ? (
                                      <p className="job-application-deadline">
                                        Application deadline: {formatJobApplicationDeadline(tabViewJob.deadline)}
                                      </p>
                                    ) : null}
                                  </div>
                                  {tabViewJob.companyLogoUrl ? (
                                    <img
                                      className="job-tab-detail-logo"
                                      src={tabViewJob.companyLogoUrl}
                                      alt={`${tabViewJob.company || "Company"} logo`}
                                      loading="lazy"
                                    />
                                  ) : null}
                                </div>
                                <JobListingContent job={tabViewJob} layout="panel" />

                                {currentUser && tabViewJob?.id ? (
                                  <JobPostCommentsSection
                                    key={String(tabViewJob.id)}
                                    jobPostId={String(tabViewJob.id)}
                                    currentUser={currentUser}
                                    authToken={authToken}
                                    layout="panel"
                                  />
                                ) : null}

                                {currentUser ? (
                                  <div className="job-actions-float">
                                    {(jobPostInteractionById[tabViewJob.id] || emptyJobPostInteraction()).applied ? (
                                      <div className="job-applied-summary">
                                        <p className="job-applied-status">
                                          {`Applied on ${
                                            formatStatusCompletedAtFromIso(
                                              (jobPostInteractionById[tabViewJob.id] || emptyJobPostInteraction())
                                                .appliedAtIso
                                            ) ||
                                            (jobPostInteractionById[tabViewJob.id] || emptyJobPostInteraction())
                                              .appliedOn ||
                                            ""
                                          }`}
                                        </p>
                                        {String(tabApplicationPitch.jobId) === String(tabViewJob.id) &&
                                        tabApplicationPitch.loaded &&
                                        tabApplicationPitch.url ? (
                                          <div className="job-applied-pitch-actions">
                                            <button
                                              type="button"
                                              className="nd1-btn nd1-btn--outline nd1-applicant-btn-sm"
                                              disabled={tabApplicationPitchDeleteBusy}
                                              onClick={() => void handleDeleteTabApplicationPitch()}
                                            >
                                              {tabApplicationPitchDeleteBusy ? "Removing…" : "Remove saved pitch video"}
                                            </button>
                                            {tabApplicationPitchDeleteError ? (
                                              <p className="apply-shine-error">{tabApplicationPitchDeleteError}</p>
                                            ) : null}
                                          </div>
                                        ) : null}
                                      </div>
                                    ) : (
                                      <div className="job-actions-float-row">
                                        <div className="job-actions-buttons">
                                          <button
                                            type="button"
                                            className={`job-action-btn job-action-btn--not-for-me${
                                              (jobPostInteractionById[tabViewJob.id] || emptyJobPostInteraction())
                                                .notForMe
                                                ? " job-action-btn--not-for-me-active"
                                                : ""
                                            }`}
                                            aria-pressed={
                                              (jobPostInteractionById[tabViewJob.id] || emptyJobPostInteraction())
                                                .notForMe
                                            }
                                            onClick={() => void toggleNotForMe(tabViewJob.id)}
                                          >
                                            <GuideIcon
                                              name={
                                                (jobPostInteractionById[tabViewJob.id] || emptyJobPostInteraction())
                                                  .notForMe
                                                  ? "check"
                                                  : "not-for-me"
                                              }
                                            />
                                            This job is not for me
                                          </button>
                                          <button
                                            type="button"
                                            className={`job-action-btn job-action-btn--save-later${
                                              (jobPostInteractionById[tabViewJob.id] || emptyJobPostInteraction()).saved
                                                ? " job-action-btn--save-later-active"
                                                : ""
                                            }`}
                                            aria-pressed={
                                              (jobPostInteractionById[tabViewJob.id] || emptyJobPostInteraction()).saved
                                            }
                                            onClick={() => void toggleSaveLater(tabViewJob.id)}
                                          >
                                            <GuideIcon
                                              name={
                                                (jobPostInteractionById[tabViewJob.id] || emptyJobPostInteraction()).saved
                                                  ? "check"
                                                  : "save"
                                              }
                                            />
                                            Save for later
                                          </button>
                                          <button
                                            type="button"
                                            className="job-action-btn primary"
                                            onClick={() => {
                                              if (!isApplicantProfileSetupComplete(currentUser)) {
                                                setShowProfileIncomplete(true);
                                                return;
                                              }
                                              captureAnalyticsEvent("application_started", {
                                                job_post_id: tabViewJob.id,
                                              });
                                              setApplyShineTargetJobId(tabViewJob.id);
                                              setShowApplyShineModal(true);
                                            }}
                                          >
                                            <GuideIcon name="applied" />
                                            Apply
                                          </button>
                                        </div>
                                        <button
                                          type="button"
                                          className="job-apply-help-trigger"
                                          onClick={() => setShowApplyHelp(true)}
                                          aria-haspopup="dialog"
                                          aria-expanded={showApplyHelp}
                                        >
                                          <GuideIcon name="info" className="job-apply-help-icon" aria-hidden="true" />
                                          <span className="job-apply-help-text">How &quot;Apply&quot; works?</span>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="job-actions-float">
                                    <p className="job-auth-prompt nd1-applicant-meta">
                                      <Link to={signInWithNextHref}>Sign in</Link> or{" "}
                                      <Link to={signUpWithNextHref}>Sign up</Link> to apply
                                    </p>
                                  </div>
                                )}
                              </article>
                            </div>
                          </>
                        ) : (
                          <p className="nd1-applicant-meta job-search-end">No results for the current filters.</p>
                        )}
                      </div>
                    )}

                    {useLiveJobSearch && hasSearched && filteredJobs.length > 0 ? (
                      <div ref={sentinelRef} className="job-search-lazy-sentinel" aria-hidden="true" />
                    ) : null}

                    {showEndMessage && filteredJobs.length > 0 ? (
                      <div className="job-search-end-of-list">
                        <p className="nd1-applicant-meta job-search-end-eyebrow">You are at the end</p>
                      </div>
                    ) : null}
                  </section>
                </>
              ) : null}
            </>
          ) : (
            <section className="nd1-applicant-section">
              <div className="nd1-applicant-section-card">
                <div className="job-search-auth">
                  <p className="nd1-applicant-empty-state__title">Job search isn&apos;t available right now.</p>
                  <p className="nd1-applicant-meta">{JOB_SEARCH_REQUIRES_API_MESSAGE}</p>
                </div>
              </div>
            </section>
          )}
      </main>

      {showProfileIncomplete ? (
        <div
          className="apply-help-overlay"
          role="presentation"
          onClick={() => setShowProfileIncomplete(false)}
        >
          <div
            className="apply-help-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Complete your profile before applying"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="apply-help-close"
              onClick={() => setShowProfileIncomplete(false)}
              aria-label="Close"
            >
              <GuideIcon name="close" />
            </button>
            <p className="profile-incomplete-message">
              Before you can apply, upload a profile photo and your CV on your profile page.
            </p>
            <div className="profile-incomplete-actions">
              <button
                type="button"
                className="large-filled-button"
                onClick={() => {
                  setShowProfileIncomplete(false);
                  navigate("/applicant-profile");
                }}
              >
                Update my profile
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showApplyHelp ? (
        <div className="apply-help-overlay" role="presentation" onClick={closeApplyHelp}>
          <div
            className="apply-help-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="apply-help-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button type="button" className="apply-help-close" onClick={closeApplyHelp} aria-label="Close">
              <GuideIcon name="close" />
            </button>
            <h2 id="apply-help-title" className="apply-help-title">
              How &quot;Apply&quot; works.
            </h2>
            <video
              ref={applyHelpVideoRef}
              className="apply-help-video"
              controls
              playsInline
              preload="metadata"
            >
              <source src={APPLY_HELP_SAMPLE_VIDEO} type="video/mp4" />
              Your browser does not support embedded video.
            </video>
            <div className="apply-help-body">
              <p>
                Because we only want genuine people like you to apply for jobs on our platform and ensure no fake
                people are able to apply, we do things differently.
              </p>
              <p>
                When you press &quot;Apply&quot; you have the opportunity to explain to the hiring manager or the
                recruiter exactly why you feel you are suitable for the job. This needs to be a short video from your
                computer or mobile. Once you do this, we will share your profile along with the pitch. This makes sure
                only genuine people will apply.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      <PitchRecordModal
        open={showApplyShineModal}
        onRequestClose={closeApplyShineModal}
        titleId="apply-shine-title"
        title="It is your time to shine..."
        introParagraphs={[
          "Tell the hiring manager why you believe you are suitable for this job. Be honest, be truthful and use examples from your experience.",
        ]}
        limitLine="Your pitch video must be 5 minutes or less."
        submitLabel="Submit application"
        onSubmitPitch={handleApplyPitchModalSubmit}
        analyticsContext="application"
        pitchVideoAriaLabel="Application pitch preview"
        showMobilePitchLink
      />

      {showSaveSearchModal ? (
        <div className="apply-help-overlay" role="presentation" onClick={handleCloseSaveSearchModal}>
          <div
            className="apply-help-modal save-search-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="save-search-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="apply-help-close"
              onClick={handleCloseSaveSearchModal}
              aria-label="Close"
              disabled={saveSearchBusy}
            >
              <GuideIcon name="close" />
            </button>
            <h3 id="save-search-title">Save this search</h3>
            {isApplicantJobSearch && savedSearchCount != null ? (
              <p className="eyebrow save-search-modal-count" aria-live="polite">
                ({savedSearchCount} out of {MAX_APPLICANT_SAVED_SEARCHES} searches saved)
              </p>
            ) : isApplicantJobSearch && savedSearchListLoading ? (
              <p className="eyebrow save-search-modal-count" aria-live="polite">
                Loading saved searches…
              </p>
            ) : null}
            {savedSearchLimitReached ? (
              <p className="form-inline-alert-danger" role="status">
                Delete a saved search to save a new one.
              </p>
            ) : null}
            <section className="job-search-panel" style={{ marginTop: 0 }}>
              <form
                className="job-search-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (savedSearchLimitReached) return;
                  void handleConfirmSaveSearch();
                }}
              >
                <div className="job-search-field-group">
                  <label htmlFor="save-search-name">Give this search a meaningful name.</label>
                  <input
                    id="save-search-name"
                    type="text"
                    value={saveSearchName}
                    onChange={(e) => setSaveSearchName(e.target.value)}
                    placeholder="e.g. UK remote product roles"
                    autoFocus
                    disabled={saveSearchBusy || savedSearchLimitReached}
                  />
                  {saveSearchError ? (
                    <p className="form-inline-alert-danger" role="alert">
                      {saveSearchError}
                    </p>
                  ) : null}
                </div>

                <div className="save-search-modal-actions">
                  <button
                    type="submit"
                    className="large-filled-button search-submit-button"
                    disabled={saveSearchBusy || savedSearchLimitReached}
                  >
                    {saveSearchBusy ? "Saving…" : "Save Search"}
                  </button>
                  <button
                    type="button"
                    className="small-outline-button job-search-reset-button"
                    onClick={handleCloseSaveSearchModal}
                    disabled={saveSearchBusy}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </section>
          </div>
        </div>
      ) : null}
    </ApplicantShell>
  );
}

export default JobSearchPage;
